import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Btn, Input, Field, Select, Modal } from "@/components/erp/ui";
import { err } from "@/components/erp/flow/FlowCommon";
import { AdBox } from "@/components/erp/social/SocialAd";
import { PostCalendar } from "@/components/erp/social/PostCalendar";
import { RefreshCw, Plus, ExternalLink } from "lucide-react";

const EMPTY = { title: "", platform: "Instagram", scheduled_at: "", caption: "", wo_id: "", assignee: "", status: "PLANNED", url: "" };
const ST = { PLANNED: "Terjadwal", POSTED: "Sudah diposting", CANCELLED: "Batal" };
const num = (n) => (n || 0).toLocaleString("id-ID");

const PostForm = ({ open, close, data, ctx, reload }) => {
  const [v, setV] = useState(EMPTY);
  useEffect(() => { if (open) setV(data ? { ...EMPTY, ...data } : { ...EMPTY, platform: ctx.platforms[0] }); }, [open, data, ctx.platforms]);
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });
  const save = async () => {
    const body = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, v[k] || ""]));
    try { data ? await api.put(`/social/posts/${data.id}`, body) : await api.post("/social/posts", body); toast.success("Postingan disimpan"); close(); reload(); } catch (e) { err(e); }
  };
  return (
    <Modal open={open} onClose={close} title={data ? "Ubah postingan" : "Jadwalkan postingan"} footer={<Btn data-testid="sp-save" onClick={save}>Simpan</Btn>}>
      <div className="grid sm:grid-cols-2 gap-3" data-testid="sp-form">
        <Field label="Judul konten"><Input data-testid="sp-title" value={v.title} onChange={set("title")} /></Field>
        <Field label="Platform"><Select data-testid="sp-platform" value={v.platform} onChange={set("platform")}>{ctx.platforms.map((p) => <option key={p}>{p}</option>)}</Select></Field>
        <Field label="Jadwal posting"><Input type="datetime-local" data-testid="sp-when" value={v.scheduled_at} onChange={set("scheduled_at")} /></Field>
        <Field label="Dari Work Order (opsional)"><Select data-testid="sp-wo" value={v.wo_id} onChange={set("wo_id")}><option value="">—</option>{ctx.wos.map((w) => <option key={w.id} value={w.id}>{w.code} · {w.product}</option>)}</Select></Field>
        <Field label="PIC"><Input data-testid="sp-assignee" value={v.assignee} onChange={set("assignee")} /></Field>
        <Field label="Status"><Select data-testid="sp-status" value={v.status} onChange={set("status")}>{Object.entries(ST).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select></Field>
      </div>
      <Field label="Caption"><textarea className="w-full border border-slate-300 rounded-lg p-2 text-sm" rows={3} data-testid="sp-caption" value={v.caption} onChange={set("caption")} /></Field>
      <Field label="Link postingan (setelah diposting)"><Input data-testid="sp-url" placeholder="https://..." value={v.url} onChange={set("url")} /></Field>
    </Modal>
  );
};

const MetricForm = ({ p, reload }) => {
  const [m, setM] = useState({ views: p.last.views, likes: p.last.likes, comments: p.last.comments, shares: p.last.shares });
  const save = async () => { try { await api.post(`/social/posts/${p.id}/metrics`, Object.fromEntries(Object.entries(m).map(([k, x]) => [k, Number(x) || 0]))); toast.success("Angka tercatat"); reload(); } catch (e) { err(e); } };
  return (
    <div className="flex flex-wrap items-end gap-2 mt-2" data-testid={`sp-metric-${p.id}`}>
      {[["views", "Views"], ["likes", "Like"], ["comments", "Komentar"], ["shares", "Share"]].map(([k, l]) => (
        <label key={k} className="text-[11px] flex flex-col">{l}<Input type="number" className="!w-24" data-testid={`sp-m-${k}-${p.id}`} value={m[k]} onChange={(e) => setM({ ...m, [k]: e.target.value })} /></label>
      ))}
      <Btn variant="ghost" data-testid={`sp-m-save-${p.id}`} onClick={save}>Catat angka</Btn>
    </div>
  );
};

const PostCard = ({ p, edit, reload }) => {
  const [open, setOpen] = useState(false);
  const refresh = async () => { try { const { data } = await api.post(`/social/posts/${p.id}/refresh`); toast.success(data.auto ? "Statistik YouTube diperbarui otomatis" : Object.keys(data.info || {}).length ? "Info link diperbarui \u2014 isi angka terbaru di bawah" : "Info link tidak tersedia dari platform \u2014 isi angka manual di bawah"); reload(); } catch (e) { err(e); } };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`sp-card-${p.id}`}>
      <div className="flex flex-wrap gap-3 items-start">
        {p.thumbnail && <img src={p.thumbnail} alt="" className="w-20 h-20 rounded-lg object-cover" />}
        <div className="flex-1 min-w-[200px]">
          <div className="text-xs text-slate-500">{p.platform} · {p.scheduled_at?.replace("T", " ")} · <b>{ST[p.status]}</b>{p.wo_code ? ` · ${p.wo_code}` : ""}{p.assignee ? ` · ${p.assignee}` : ""}</div>
          <div className="font-semibold">{p.title}</div>
          {p.url && <a href={p.url} target="_blank" rel="noreferrer" className="text-xs text-sky-700 inline-flex items-center gap-1">{p.link_title || p.url} <ExternalLink className="w-3 h-3" /></a>}
          <div className="text-xs mt-1" data-testid={`sp-last-${p.id}`}>{num(p.last.views)} views · {num(p.last.likes)} like · {num(p.last.comments)} komentar · {num(p.last.shares)} share</div>
        </div>
        <div className="flex gap-2">
          <Btn variant="ghost" data-testid={`sp-edit-${p.id}`} onClick={edit}>Ubah</Btn>
          {p.url && <Btn variant="ghost" data-testid={`sp-refresh-${p.id}`} onClick={refresh}><RefreshCw className="w-4 h-4" /></Btn>}
          {p.status === "POSTED" && <Btn variant="ghost" data-testid={`sp-toggle-${p.id}`} onClick={() => setOpen(!open)}>{open ? "Tutup" : "Angka & iklan"}</Btn>}
        </div>
      </div>
      {open && <><MetricForm p={p} reload={reload} /><AdBox p={p} reload={reload} /></>}
    </div>
  );
};

export const SocialPlan = () => {
  const [d, setD] = useState(null); const [form, setForm] = useState({ open: false });
  const load = () => api.get("/social/posts").then(({ data }) => setD(data)).catch(err);
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const up = d.posts.filter((p) => p.status === "PLANNED"); const done = d.posts.filter((p) => p.status === "POSTED");
  return (
    <div data-testid="sp-page">
      <Btn data-testid="sp-add" onClick={() => setForm({ open: true })}><Plus className="w-4 h-4" /> Jadwalkan postingan</Btn>
      <PostCalendar posts={d.posts} events={d.events || []} onOpen={(p) => setForm({ open: true, data: p })} />
      <h3 className="font-semibold text-sm mt-5 mb-2">Akan diposting ({up.length})</h3>
      <div className="space-y-2">{up.map((p) => <PostCard key={p.id} p={p} reload={load} edit={() => setForm({ open: true, data: p })} />)}</div>
      <h3 className="font-semibold text-sm mt-5 mb-2">Sudah diposting ({done.length})</h3>
      <div className="space-y-2">{done.map((p) => <PostCard key={p.id} p={p} reload={load} edit={() => setForm({ open: true, data: p })} />)}</div>
      {!d.posts.length && <p className="text-sm text-slate-400 mt-3" data-testid="sp-empty">Belum ada jadwal posting.</p>}
      <PostForm open={form.open} data={form.data} ctx={d} close={() => setForm({ open: false })} reload={load} />
      {d.is_owner && <Platforms list={d.platforms} reload={load} />}
    </div>
  );
};

const Platforms = ({ list, reload }) => {
  const [t, setT] = useState(list.join(", "));
  const save = async () => { try { await api.put("/social/platforms", t.split(",")); toast.success("Platform disimpan"); reload(); } catch (e) { err(e); } };
  return (
    <details className="rounded-2xl border border-slate-200 bg-white p-4 mt-6" data-testid="sp-platforms">
      <summary className="font-semibold cursor-pointer text-sm">Daftar platform (owner)</summary>
      <Input className="mt-2" data-testid="sp-platforms-input" value={t} onChange={(e) => setT(e.target.value)} />
      <p className="text-[11px] text-slate-500">Pisahkan dengan koma, mis. Instagram, TikTok, YouTube, Facebook, Shopee Video</p>
      <Btn className="mt-2" data-testid="sp-platforms-save" onClick={save}>Simpan</Btn>
    </details>
  );
};
